import { useState } from 'react';
import { useLottery } from '../../../hooks/useLottery';

export default function LotteryManagementPanel() {
  const { items, loading, addItem, updateItem, deleteItem } = useLottery();
  const [newContent, setNewContent] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const showFeedback = (msg: string) => { setFeedback(msg); setTimeout(() => setFeedback(null), 2000); };

  const handleAdd = async () => {
    if (!newContent.trim() || adding) return;
    setAdding(true);
    await addItem(newContent.trim());
    setNewContent('');
    setAdding(false);
    showFeedback('✓ 已新增');
  };

  const handleEdit = (id: string, content: string) => { setEditingId(id); setEditContent(content); };

  const handleEditSave = async (id: string) => {
    if (!editContent.trim()) return;
    await updateItem(id, editContent.trim());
    setEditingId(null);
    showFeedback('✓ 已更新');
  };

  const handleDelete = async (id: string) => {
    await deleteItem(id);
    showFeedback('✓ 已刪除');
  };

  const canAdd = newContent.trim().length > 0;

  return (
    <div className="flex-1 px-5 py-5 space-y-5">

      {/* Header card */}
      <div className="rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #1a1200, #0d0d0d)', border: '1px solid rgba(212,168,67,0.2)' }}>
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.4), transparent)' }} />
        <div className="px-4 py-3.5 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
            style={{ background: '#d4a84318', border: '1px solid #d4a84335' }}>
            <i className="ri-lottery-line text-[#d4a843] text-lg" />
          </div>
          <div>
            <p className="text-[#d4a843] text-xs font-black tracking-[0.4em] uppercase font-cinzel">抽籤池管理</p>
            <p className="text-[#555] text-xs mt-0.5">
              共 <span className="text-[#d4a843] font-bold">{items.length}</span> 支籤在池中
            </p>
          </div>
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#7ec8a0]/20 bg-[#7ec8a0]/05">
          <i className="ri-check-circle-fill text-[#7ec8a0]" />
          <span className="text-[#7ec8a0] text-sm font-bold">{feedback}</span>
        </div>
      )}

      {/* Add new */}
      <div>
        <p className="text-[#444] text-[10px] tracking-[0.35em] uppercase mb-2">新增一支籤</p>
        <div className="flex items-center gap-2.5 bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-3.5 py-2.5 focus-within:border-[#2a2a2a] transition-colors mb-2.5">
          <i className="ri-quill-pen-line text-[#333] text-sm shrink-0" />
          <input type="text" value={newContent}
            onChange={e => setNewContent(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="輸入籤的內容，例：帶大家玩一個遊戲"
            maxLength={60}
            className="bg-transparent text-white text-sm w-full outline-none placeholder:text-[#282828]"
          />
          {newContent && (
            <span className="text-[#2a2a2a] text-[10px] shrink-0">{newContent.length}/60</span>
          )}
        </div>
        <button onClick={handleAdd} disabled={!canAdd || adding}
          className="w-full py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          style={{
            background: canAdd ? 'linear-gradient(135deg, #d4a843, #c89030)' : '#0f0f0f',
            color: canAdd ? '#0d0d0d' : '#2a2a2a',
            border: `1px solid ${canAdd ? 'transparent' : '#141414'}`,
            boxShadow: canAdd ? '0 0 20px rgba(212,168,67,0.3)' : 'none',
          }}
        >
          <i className={adding ? 'ri-loader-4-line animate-spin' : 'ri-add-line'} />
          {adding ? '新增中…' : '加入籤池'}
        </button>
      </div>

      {/* List */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-[#444] text-[10px] tracking-[0.35em] uppercase">籤池清單</p>
          <span className="text-[10px] font-black px-2 py-0.5 rounded-full font-cinzel"
            style={{ background: '#d4a84312', color: '#d4a84360', border: '1px solid #d4a84320' }}>
            {items.length} 支
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-[#222] text-xs">
            <i className="ri-loader-4-line animate-spin" />載入中…
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl"
              style={{ background: '#d4a84308', border: '1.5px dashed #d4a84320' }}>
              <i className="ri-inbox-line text-xl" style={{ color: '#d4a84340' }} />
            </div>
            <div className="text-center">
              <p className="text-[#222] text-xs">籤池是空的</p>
              <p className="text-[#1a1a1a] text-xs mt-0.5">新增第一支籤開始吧</p>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
            {items.map((item, idx) => (
              <div key={item.id} className="rounded-xl overflow-hidden border border-[#141414] transition-all"
                style={{ background: '#0a0a0a' }}>
                {editingId === item.id ? (
                  <div className="p-3">
                    <div className="flex items-center gap-2 bg-[#111] border border-[#d4a843]/25 rounded-xl px-3 py-2 mb-2.5">
                      <i className="ri-quill-pen-line text-[#d4a84360] text-sm shrink-0" />
                      <input type="text" value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleEditSave(item.id); if (e.key === 'Escape') setEditingId(null); }}
                        maxLength={60} autoFocus
                        className="bg-transparent text-white text-sm w-full outline-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditingId(null)}
                        className="flex-1 py-2 rounded-xl text-xs text-[#555] border border-[#1e1e1e] cursor-pointer whitespace-nowrap hover:text-[#888] transition-colors">
                        取消
                      </button>
                      <button onClick={() => handleEditSave(item.id)}
                        className="flex-1 py-2 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                        style={{ background: '#d4a843', color: '#0d0d0d' }}>
                        <i className="ri-check-line" />儲存
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2.5 px-3 py-2.5">
                    {/* Number badge */}
                    <span className="w-6 h-6 flex items-center justify-center rounded-lg text-[9px] font-black shrink-0 font-cinzel"
                      style={{ background: '#d4a84312', color: '#d4a84360', border: '1px solid #d4a84320' }}>
                      {idx + 1}
                    </span>
                    <span className="text-[#aaa] text-sm flex-1 min-w-0 truncate">{item.content}</span>
                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => handleEdit(item.id, item.content)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all text-[#2a2a2a] hover:text-[#d4a843] hover:bg-[#d4a843]/08">
                        <i className="ri-pencil-line text-xs" />
                      </button>
                      <button onClick={() => handleDelete(item.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all text-[#2a2a2a] hover:text-[#e05555] hover:bg-[#e05555]/08">
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <p className="text-[#1e1e1e] text-[10px] text-center tracking-wide">
          <i className="ri-information-line mr-1" />點 NavBar「抽籤」即可從此籤池隨機抽出
        </p>
      )}
    </div>
  );
}
